import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { usePublishToEmma } from '../hooks/usePublishToEmma';
import axios from 'axios';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell,
} from 'recharts';
import {
  ArrowLeft, BarChart3, Loader, RefreshCw, Building2, TrendingUp,
  CheckCircle, AlertTriangle, XCircle, Lightbulb, Link2,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const KPI_COLORS = {
  GOED: { color: '#22c55e', bg: 'rgba(34,197,94,0.13)', icon: CheckCircle },
  AANDACHT: { color: '#f59e0b', bg: 'rgba(245,158,11,0.13)', icon: AlertTriangle },
  RISICO: { color: '#ef4444', bg: 'rgba(239,68,68,0.13)', icon: XCircle },
};

const PIE_COLORS = ['#3b82f6', '#ef4444', '#22c55e'];

const FinancialDashboard = () => {
  const { token } = useAuth();
  const { lang } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [hasData, setHasData] = useState(null);
  const [acctAvailable, setAcctAvailable] = useState(false);
  const [dataSource, setDataSource] = useState(null);
  const [quickMode, setQuickMode] = useState(false);
  const [quickData, setQuickData] = useState({
    revenue: '', costs: '', employees: '', growth_rate: '', cash: '',
  });

  
  // Build a comprehensive summary of ALL visible data for Emma
  const emmaContext = React.useMemo(() => {
    if (!data) return null;
    if (data.raw_analysis) return data.raw_analysis;
    const parts = [];
    if (data.health_score) parts.push(`Financiele Gezondheid Score: ${data.health_score}/100`);
    if (data.summary) parts.push(`Samenvatting: ${data.summary}`);
    const kpis = data.kpis || [];
    if (kpis.length) {
      parts.push('KPIs:');
      kpis.forEach(k => parts.push(`  - ${k.label}: ${k.value}${k.unit || ''} (sector gemiddelde: ${k.benchmark}${k.unit || ''}) — ${k.status === 'good' ? 'Goed' : k.status === 'warning' ? 'Aandacht' : 'Kritiek'}: ${k.insight || ''}`));
    }
    const rb = data.revenue_breakdown || {};
    if (rb.revenue) parts.push(`Omzetverdeling: Omzet EUR ${rb.revenue?.toLocaleString()}, Kosten EUR ${rb.costs?.toLocaleString()}, Winst EUR ${rb.profit?.toLocaleString()}`);
    const monthly = data.monthly_projection || [];
    if (monthly.length) {
      parts.push('Maandelijkse Prognose:');
      monthly.forEach(m => parts.push(`  - ${m.month}: Omzet EUR ${m.revenue?.toLocaleString()}, Kosten EUR ${m.costs?.toLocaleString()}`));
    }
    const opts = data.optimizations || [];
    if (opts.length) {
      parts.push('Optimalisatie Suggesties:');
      opts.forEach(o => parts.push(`  - [${o.priority}] ${o.title}: ${o.description} (Impact: ${o.impact})`));
    }
    return (Array.isArray(parts) ? parts : []).join('\n');
  }, [data]);

  usePublishToEmma('AI Financial Dashboard', emmaContext);
  const headers = { Authorization: `Bearer ${token}` };
  const isNL = lang === 'nl';

  useEffect(() => { checkData(); }, []);

  const checkData = async () => {
    try {
      const [profileRes, acctRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/compass/company/profile`, { headers }).catch(() => null),
        axios.get(`${BACKEND_URL}/api/compass/cashflow/accounting-check`, { headers }).catch(() => null),
      ]);
      const hasProfile = !!profileRes?.data?.profile?.annual_revenue;
      const hasAcct = !!acctRes?.data?.has_data;
      setHasData(hasProfile || hasAcct);
      setAcctAvailable(hasAcct);
    } catch { setHasData(false); }
  };

  const runAnalysis = async () => {
    setLoading(true); setData(null);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/compass/financial/analyze`, { language: lang, use_accounting_data: true }, { headers });
      if (!res.data.has_data) { setHasData(false); setLoading(false); return; }
      setData(res.data.data);
      setDataSource(res.data.data_source || 'profile');
    } catch (err) {
      alert(err.response?.data?.detail || 'Analysis failed');
    } finally { setLoading(false); }
  };

  const runQuickAnalysis = async () => {
    setLoading(true); setData(null);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/compass/financial/quick-analysis`, {
        revenue: quickData.revenue ? Number(quickData.revenue) : null,
        costs: quickData.costs ? Number(quickData.costs) : null,
        employees: quickData.employees ? Number(quickData.employees) : null,
        growth_rate: quickData.growth_rate ? Number(quickData.growth_rate) : null,
        cash_reserves: quickData.cash ? Number(quickData.cash) : null,
        language: lang,
      }, { headers });
      setData({ raw_analysis: res.data.analysis });
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed');
    } finally { setLoading(false); }
  };

  const fmtEUR = (v) => `EUR ${Number(v || 0).toLocaleString('nl-NL')}`;

  const renderStructured = () => {
    if (!data) return null;
    if (data.raw_analysis) {
      return <div className="auto-input-card"><div className="markdown-content" style={{ whiteSpace: 'pre-wrap' }}>{data.raw_analysis}</div></div>;
    }

    const kpis = data.kpis || [];
    const rb = data.revenue_breakdown || {};
    const monthly = data.monthly_projection || [];
    const opts = data.optimizations || [];

    const pieData = [
      { name: isNL ? 'Omzet' : 'Revenue', value: rb.revenue || 0 },
      { name: isNL ? 'Kosten' : 'Costs', value: rb.costs || 0 },
      { name: isNL ? 'Winst' : 'Profit', value: rb.profit || 0 },
    ].filter(d => d.value > 0);

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }} data-testid="fin-result">
        {/* Health Score + Summary */}
        <div className="auto-input-card" style={{ display: 'flex', alignItems: 'center', gap: '2rem', padding: '1.5rem' }}>
          <div style={{
            width: 80, height: 80, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: (data.health_score || 0) >= 70 ? '#16202C' : (data.health_score || 0) >= 50 ? '#16202C' : '#16202C',
            border: `3px solid ${(data.health_score || 0) >= 70 ? '#22c55e' : (data.health_score || 0) >= 50 ? '#f59e0b' : '#ef4444'}`,
          }}>
            <span style={{
              fontSize: '1.75rem', fontWeight: 700,
              color: (data.health_score || 0) >= 70 ? '#22c55e' : (data.health_score || 0) >= 50 ? '#f59e0b' : '#ef4444',
            }}>{data.health_score || '?'}</span>
          </div>
          <div style={{ flex: 1 }}>
            <h2 className="heading-2" style={{ marginBottom: '0.25rem' }}>
              {isNL ? 'Financiele Gezondheid' : 'Financial Health'}
            </h2>
            <p className="body-small text-secondary">{data.summary || ''}</p>
          </div>
        </div>

        {/* KPI Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1rem' }}>
          {(Array.isArray(kpis) ? kpis : []).map((kpi, i) => {
            const cfg = KPI_COLORS[kpi.status] || KPI_COLORS.AANDACHT;
            const Icon = cfg.icon;
            return (
              <div key={i} className="auto-input-card" style={{
                padding: '1.25rem', borderTop: `3px solid ${cfg.color}`, background: cfg.bg,
              }} data-testid={`kpi-${i}`}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span className="caption text-secondary" style={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{kpi.name}</span>
                  <Icon size={16} style={{ color: cfg.color }} />
                </div>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-primary)', marginTop: '0.5rem' }}>{kpi.value}</div>
                <div className="caption" style={{ color: cfg.color, marginTop: '0.25rem' }}>{kpi.benchmark}</div>
                <p className="caption text-secondary" style={{ marginTop: '0.25rem' }}>{kpi.detail}</p>
              </div>
            );
          })}
        </div>

        {/* Charts Row */}
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1rem' }}>
          {/* Monthly Revenue/Costs Bar Chart */}
          {monthly.length > 0 && (
            <div className="auto-input-card" style={{ padding: '1.25rem' }}>
              <h3 className="heading-3" style={{ marginBottom: '1rem' }}>
                {isNL ? 'Maandelijkse Prognose' : 'Monthly Projection'}
              </h3>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={monthly} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border-light)" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${(v/1000).toFixed(0)}K`} />
                  <Tooltip formatter={(v) => fmtEUR(v)} />
                  <Legend />
                  <Bar dataKey="revenue" name={isNL ? 'Omzet' : 'Revenue'} fill="#3b82f6" radius={[4,4,0,0]} />
                  <Bar dataKey="costs" name={isNL ? 'Kosten' : 'Costs'} fill="#f59e0b" radius={[4,4,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Revenue Breakdown Pie */}
          {pieData.length > 0 && (
            <div className="auto-input-card" style={{ padding: '1.25rem' }}>
              <h3 className="heading-3" style={{ marginBottom: '1rem' }}>
                {isNL ? 'Verdeling' : 'Breakdown'}
              </h3>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={80}
                    paddingAngle={3} dataKey="value" label={({ name, percent }) => `${name} ${(percent*100).toFixed(0)}%`}>
                    {(Array.isArray(pieData) ? pieData : []).map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => fmtEUR(v)} />
                </PieChart>
              </ResponsiveContainer>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem', marginTop: '0.5rem' }}>
                {(Array.isArray(pieData) ? pieData : []).map((d, i) => (
                  <span key={i} className="caption" style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: PIE_COLORS[i] }} />
                    {d.name}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Optimizations */}
        {opts.length > 0 && (
          <div className="auto-input-card" style={{ padding: '1.25rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
              <Lightbulb size={18} style={{ color: '#f59e0b' }} />
              <h3 className="heading-3" style={{ margin: 0 }}>{isNL ? 'Optimalisatie Kansen' : 'Optimization Opportunities'}</h3>
            </div>
            {(Array.isArray(opts) ? opts : []).map((o, i) => (
              <div key={i} style={{
                padding: '0.75rem', marginBottom: '0.5rem', borderRadius: '0.5rem',
                background: 'var(--bg-section)', borderLeft: '3px solid #3b82f6',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span className="body-small" style={{ fontWeight: 600 }}>{o.title}</span>
                  <span className="caption" style={{ color: '#3b82f6', fontWeight: 600 }}>{o.impact}</span>
                </div>
                <p className="caption text-secondary" style={{ marginTop: '0.25rem' }}>{o.description}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="tool-page">
      <div className="tool-header">
        <button onClick={() => navigate('/portal/ai-tools')} className="btn-secondary" data-testid="back-btn">
          <ArrowLeft size={16} /> AI Tools
        </button>
        <h1 className="heading-1">
          <BarChart3 size={22} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 8 }} />
          {isNL ? 'Financieel Dashboard' : 'Financial Dashboard'}
        </h1>
      </div>
      <div className="tool-content" style={{ maxWidth: '1000px' }}>
        <p className="body-medium text-secondary" style={{ marginBottom: '1.5rem' }}>
          {isNL ? 'AI-analyse van je financiele KPIs met grafieken en optimalisaties' : 'AI analysis of your financial KPIs with charts and optimizations'}
        </p>

        {!data && !loading && (
          <div className="auto-start-section" data-testid="fin-start">
            {acctAvailable && (
              <div className="acct-data-banner" data-testid="fin-acct-banner">
                <Link2 size={16} />
                <span>{isNL ? 'Boekhouddata beschikbaar — analyse wordt verrijkt met echte cijfers uit je boekhoudsoftware.' : 'Accounting data available — analysis will use real numbers from your accounting software.'}</span>
              </div>
            )}
            {hasData ? (
              <div className="auto-input-card">
                <button className="btn-primary" style={{ width: '100%' }}
                  onClick={runAnalysis} data-testid="run-analysis-btn">
                  <BarChart3 size={16} /> {isNL ? (acctAvailable ? 'Start Analyse (met boekhouddata)' : 'Start Financiele Analyse') : (acctAvailable ? 'Run Analysis (with accounting data)' : 'Run Financial Analysis')}
                </button>
              </div>
            ) : hasData === false ? (
              <div className="auto-input-card">
                <p className="body-small text-secondary">{isNL ? 'Vul je financiele gegevens in bij je bedrijfsprofiel of synchroniseer je boekhoudsoftware.' : 'Fill in financial data in your profile or sync your accounting software.'}</p>
                <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem', flexWrap: 'wrap' }}>
                  <button className="btn-primary" style={{ flex: 1 }}
                    onClick={() => navigate('/portal/company-profile')} data-testid="go-profile-btn">
                    <Building2 size={16} /> {isNL ? 'Bedrijfsprofiel' : 'Company Profile'}
                  </button>
                  <button className="btn-secondary" style={{ flex: 1 }}
                    onClick={() => navigate('/portal/accounting')} data-testid="go-accounting-btn">
                    <Link2 size={16} /> {isNL ? 'Boekhouding Koppelen' : 'Connect Accounting'}
                  </button>
                </div>
                <button className="btn-secondary" style={{ width: '100%', marginTop: '0.75rem' }}
                  onClick={() => setQuickMode(true)} data-testid="quick-mode-btn">
                  {isNL ? 'Snelle Analyse' : 'Quick Analysis'}
                </button>
              </div>
            ) : null}
            {quickMode && (
              <div className="auto-input-card" data-testid="quick-form">
                <h3 className="heading-3">{isNL ? 'Snelle Analyse' : 'Quick Analysis'}</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginTop: '1rem' }}>
                  {[['revenue', isNL ? 'Omzet' : 'Revenue'], ['costs', isNL ? 'Kosten' : 'Costs'], ['employees', isNL ? 'Medewerkers' : 'Employees'], ['growth_rate', isNL ? 'Groei %' : 'Growth %']].map(([k, l]) => (
                    <div className="form-group" key={k}>
                      <label className="body-small" style={{ fontWeight: 600 }}>{l}</label>
                      <input type="number" className="form-input" value={quickData[k]}
                        onChange={(e) => setQuickData({...quickData, [k]: e.target.value})} />
                    </div>
                  ))}
                </div>
                <button className="btn-primary" style={{ width: '100%', marginTop: '0.75rem' }}
                  onClick={runQuickAnalysis} data-testid="quick-analyze-btn">
                  <BarChart3 size={16} /> {isNL ? 'Analyseer' : 'Analyze'}
                </button>
              </div>
            )}
          </div>
        )}

        {loading && (
          <div className="auto-input-card" style={{ textAlign: 'center', padding: '3rem' }}>
            <Loader className="spin" size={24} style={{ margin: '0 auto 1rem' }} />
            <p className="body-medium">{isNL ? 'AI analyseert je financien...' : 'AI analyzing...'}</p>
          </div>
        )}

        {data && renderStructured()}

        {data && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '1.5rem', flexWrap: 'wrap' }}>
            {dataSource === 'accounting' && (
              <span className="acct-source-tag" data-testid="fin-source-tag">
                <Link2 size={12} /> {isNL ? 'Gebaseerd op boekhouddata' : 'Based on accounting data'}
              </span>
            )}
            <button className="btn-secondary"
              onClick={() => { setData(null); setQuickMode(false); setDataSource(null); }} data-testid="new-analysis-btn">
              <RefreshCw size={16} /> {isNL ? 'Nieuwe Analyse' : 'New Analysis'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default FinancialDashboard;
